import PageHero from "@/components/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { SectionLabel } from "@/components/primitives";
import CorporateInfoCard from "@/components/CorporateInfoCard";
import CTABlock from "@/components/CTABlock";
import { credibility } from "@/data/content";

const engagement = [
  { step: "01", title: "Discovery", desc: "We start with a strategy conversation to understand the business, its goals, and the gaps in how it currently operates." },
  { step: "02", title: "Diagnosis", desc: "We map the offer, sales process, systems, and operating model to identify the highest-leverage priorities." },
  { step: "03", title: "Implementation", desc: "We build and implement the required infrastructure — website, CRM, automation, and AI systems." },
  { step: "04", title: "Refinement", desc: "We monitor performance, remove bottlenecks, and improve how the business runs over time." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        testId="about-page-hero"
        label="About Us"
        title="Built by Fortunastrive"
        accent="Services Private Limited."
        subtitle="A consulting and systems implementation company helping service businesses improve how they position, market, automate, and operate."
      />

      {/* Who / what */}
      <section className="border-t border-fs-border bg-fs-bg2 py-20 lg:py-28">
        <div className="fs-shell grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <h2 className="fs-h3">Who we are</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 fs-body">
                Fortuna Strive is a systems-led consulting firm. We work with
                service businesses that have ambition but feel held back by unclear
                positioning, manual processes, and disconnected tools.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <h2 className="fs-h3">The problems we solve</h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-5 fs-body">
                Weak positioning, founder-dependent sales, inconsistent follow-up,
                and operations that do not scale. We replace friction with clear,
                connected systems designed for repeatable execution.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* How engagements work */}
      <section className="border-t border-fs-border bg-fs-bg py-20 lg:py-28">
        <div className="fs-shell">
          <Reveal>
            <SectionLabel>How engagements work</SectionLabel>
          </Reveal>
          <StaggerGroup className="mt-12 grid gap-px border border-fs-border bg-fs-border sm:grid-cols-2 lg:grid-cols-4">
            {engagement.map((e) => (
              <StaggerItem key={e.step}>
                <div className="h-full bg-fs-bg p-7">
                  <span className="font-serif text-3xl text-fs-gold/70">{e.step}</span>
                  <h3 className="mt-4 font-serif text-xl text-fs-ink">{e.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-fs-muted">{e.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Principles */}
      <section className="border-t border-fs-border bg-fs-bg2 py-20 lg:py-28">
        <div className="fs-shell">
          <Reveal>
            <SectionLabel>Our principles</SectionLabel>
          </Reveal>
          <StaggerGroup className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {credibility.map((c) => {
              const Icon = c.icon;
              return (
                <StaggerItem key={c.title}>
                  <Icon className="h-5 w-5 text-fs-gold" strokeWidth={1.4} />
                  <h3 className="mt-4 font-serif text-xl text-fs-ink">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-fs-muted">{c.desc}</p>
                </StaggerItem>
              );
            })}
          </StaggerGroup>
        </div>
      </section>

      {/* Company details */}
      <section className="border-t border-fs-border bg-fs-bg py-20 lg:py-28">
        <div className="fs-shell">
          <Reveal>
            <SectionLabel>Company details</SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 max-w-3xl">
              <CorporateInfoCard />
            </div>
          </Reveal>
        </div>
      </section>

      <CTABlock />
    </div>
  );
}
